import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { GraduationCap } from 'lucide-react';

interface TrainingSection {
  id: string;
  title: string;
  video_url: string | null;
  body: string;
  sort_order: number;
}

interface TrainingConfig {
  title: string;
  description: string;
  welcome_video_url: string | null;
  helpdesk_url: string | null;
  helpdesk_label: string | null;
}

function parseVideoEmbed(url: string): { embedUrl: string } | null {
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  if (ytMatch) {
    return { embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}` };
  }
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return { embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
  }
  return null;
}

const Training = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<TrainingSection[]>([]);
  const [config, setConfig] = useState<TrainingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState<string | null>(null);

  const handleContentClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (href && href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
      }
    },
    [navigate],
  );

  useEffect(() => {
    const fetchData = async () => {
      const [sectionsRes, configRes] = await Promise.all([
        supabase
          .from('training_sections')
          .select('id, title, video_url, body, sort_order')
          .eq('is_published', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('training_config')
          .select('title, description, welcome_video_url, helpdesk_url, helpdesk_label')
          .single(),
      ]);

      if (!sectionsRes.error && sectionsRes.data) setSections(sectionsRes.data);
      if (!configRes.error && configRes.data) setConfig(configRes.data);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className='container mx-auto p-6'>
        <div className='text-muted-foreground text-center py-12'>Loading...</div>
      </div>
    );
  }

  const welcomeVideo = config?.welcome_video_url
    ? parseVideoEmbed(config.welcome_video_url)
    : null;

  return (
    <div className='container mx-auto p-6 max-w-4xl space-y-8'>
      {/* Header */}
      <div className='flex items-center gap-3'>
        <GraduationCap className='h-8 w-8 text-primary' />
        <div>
          <h1 className='text-3xl font-bold'>{config?.title || 'Training Center'}</h1>
          {config?.description && (
            <p className='text-muted-foreground'>{config.description}</p>
          )}
        </div>
      </div>

      {/* Welcome video */}
      {welcomeVideo && (
        <div
          className='relative w-full rounded-lg overflow-hidden'
          style={{ paddingBottom: '56.25%' }}
        >
          <iframe
            src={welcomeVideo.embedUrl}
            className='absolute inset-0 w-full h-full'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
            title='Welcome'
          />
        </div>
      )}

      {/* Sections */}
      {sections.length === 0 ? (
        <p className='text-muted-foreground'>
          No training content available yet. Check back soon.
        </p>
      ) : (
        <div className='space-y-2'>
          {sections.map((section, index) => {
            const isOpen = openSection === section.id;
            const video = section.video_url ? parseVideoEmbed(section.video_url) : null;

            return (
              <div
                key={section.id}
                className='border border-border rounded-lg overflow-hidden'
              >
                <button
                  onClick={() => setOpenSection(isOpen ? null : section.id)}
                  className='w-full flex items-center justify-between px-5 py-4 bg-card hover:bg-accent/50 transition-colors text-left'
                >
                  <div className='flex items-center gap-3'>
                    <span className='text-muted-foreground text-sm font-mono w-6'>
                      {index + 1}.
                    </span>
                    <span className='font-medium'>{section.title}</span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 9l-7 7-7-7'
                    />
                  </svg>
                </button>

                {isOpen && (
                  <div className='px-5 py-5 border-t border-border bg-background'>
                    {video && (
                      <div className='mb-5'>
                        <div
                          className='relative w-full'
                          style={{ paddingBottom: '56.25%' }}
                        >
                          <iframe
                            src={video.embedUrl}
                            className='absolute inset-0 w-full h-full rounded-lg'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                            allowFullScreen
                            title={section.title}
                          />
                        </div>
                      </div>
                    )}
                    {section.body && (
                      <div
                        className='prose prose-sm dark:prose-invert max-w-none prose-a:text-primary'
                        onClick={handleContentClick}
                        dangerouslySetInnerHTML={{ __html: section.body }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Help desk link */}
      {config?.helpdesk_url && (
        <div className='pt-6 border-t border-border'>
          <a
            href={config.helpdesk_url}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-2 text-primary hover:underline'
          >
            <span>
              {config.helpdesk_label || 'For additional help visit our help desk'}
            </span>
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
              />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};

export default Training;
